import { CountryIso3166, RiskScore } from "@tfg/db"
import { z } from "zod"

import { sortValueSchema } from "@tfg/types/schemas/traceabilityRequests"
import {
  removeUndefined,
  standardEmail,
  stringToJSONSchema,
} from "@tfg/types/schemas/utils"

import { ContactRequests } from "../../endpoints/contacts"
import { CountryIso3166Schema, LanguageIso6391Schema } from "../enum"

import { hasAliasOrTypeId } from "./utils"

export const MappingTypesEnum = z.enum(["PRODUCT", "MATERIAL", "STANDARD"])
export const typeMappingUpdateSchema = z
  .object({
    alias: z.string(),
    typeId: z.number(),
    type: MappingTypesEnum,
  })
  .refine((data) => hasAliasOrTypeId(data.alias, data.typeId), {
    error: "At least one of alias or productTypeId must be provided.",
  })
export type typeMappingUpdateValidationType = z.infer<
  typeof typeMappingUpdateSchema
>

export const SupplierTypeSchema = z.enum([
  "all",
  "direct",
  "indirect",
  "indirectConfidential",
])

export type SupplierTypeValidationType = z.infer<typeof SupplierTypeSchema>

const orderBySchemaBase = z
  .object({
    name: sortValueSchema,
    company: sortValueSchema,
    pendingProductrequests: sortValueSchema,
    // pendingTransactionCertificaterequests: sortValueSchema,
    // pendingScopeCertificaterequests: sortValueSchema,
    pendingProductionSiterequests: sortValueSchema,
    completedProductrequests: sortValueSchema,
    // completedTransactionCertificaterequests: sortValueSchema,
    // completedScopeCertificaterequests: sortValueSchema,
    completedProductionSiterequests: sortValueSchema,
    email: sortValueSchema,
    averageResponseTime: sortValueSchema,
    lastConnectionDate: sortValueSchema,
    lastReminderDate: sortValueSchema,
    // ratio: sortValueSchema,
  })
  .partial()

export const orderBySchema = z
  .any()
  .transform((data) => orderBySchemaBase.parse(data))
  .transform((data) => removeUndefined(data))

export type orderByValidationType = z.infer<typeof orderBySchema>

const supplierMonitoringFiltersSchemaBase = z.object({
  supplierType: SupplierTypeSchema.optional(),
  searchSupplier: z.string().optional(),
  country: z.enum(CountryIso3166).optional(),
})

export const supplierMonitoringFiltersSchema = z
  .any()
  .transform((data) => supplierMonitoringFiltersSchemaBase.parse(data))
  .transform((data) => removeUndefined(data))

export type supplierMonitoringFiltersValidationType = z.infer<
  typeof supplierMonitoringFiltersSchema
>

const supplierRiskFiltersSchemaBase = z.object({
  riskCertification: z.enum(RiskScore).optional(),
  riskCollaboration: z.enum(RiskScore).optional(),
})

export const supplierRiskFiltersSchema = z
  .any()
  .transform((data) => supplierRiskFiltersSchemaBase.parse(data))
  .transform((data) => removeUndefined(data))

export type supplierRiskFiltersValidationType = z.infer<
  typeof supplierRiskFiltersSchema
>

export const riskQuerySchema = z.object({
  skip: z.coerce.number().optional(),
  take: z.coerce.number().optional(),
  filters: stringToJSONSchema.pipe(supplierRiskFiltersSchema).optional(),
  searchSupplier: z.string().optional(),
})

export type riskQueryValidationType = z.infer<typeof riskQuerySchema>

export const paramsIdSchema = z.object({
  userId: z.uuid(),
})

export const transferContactSchema = z.object({
  email: standardEmail(),
  name: z.string().trim().min(1),
  organizationName: z.string(),
  organizationCountry: CountryIso3166Schema,
  language: LanguageIso6391Schema,
  oldContactUserId: z.uuid(),
}) satisfies z.ZodType<ContactRequests.TransferContact>

export type transferContactValidationType = z.infer<
  typeof transferContactSchema
>

export const SupplierGetQuerySchema = z.object({
  skip: z.coerce.number().optional(),
  take: z.coerce.number().optional(),
  filters: stringToJSONSchema.pipe(supplierMonitoringFiltersSchema).optional(),
  searchSupplier: z.string().optional(),
  orderBy: stringToJSONSchema.pipe(orderBySchema).optional(),
})

export type SupplierGetQueryValidationType = z.infer<
  typeof SupplierGetQuerySchema
>
